import { CheckSquareFilled, EditOutlined, LinkOutlined, ClockCircleOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons/lib/components/Icon';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input, Form } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';

import { WorkspaceAccessResponse } from '../../../api/workspace';
import ChevronLeftSvg from '../../../assets/icons/chevron-left.svg?react';
import { SubitemIcon } from '../../../assets/CustomIcons/SubitemIcon';
import { SprintIcon } from '../../../assets/CustomIcons/SprintIcon';
import colorTokens from '../../../constants/design_system/colors';
import { getWebsiteDomain } from '../../../authConfig';
import { ModalContext, ModalType } from '../../../context/ModalContext';
import { buildAddSubitemModalProps } from '../../Modal/createItemSpace';
import { useNavigateToTask } from '../../../hooks/useTaskNavigation';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { Epic } from '../../../types/Epic';
import { SpaceLabel } from '../../../types/TaskLabel';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup } from '../../../types/TaskStatus';
import { TaskType } from '../../../types/TaskType';
import { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType } from '../../../types/WorkspaceView';
import { Column } from '../../../types/WorkspaceViewColumn';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';
import { COLUMN_WIDTHS } from '../../TaskTable/constants/columnWidths';
import { PriorityOptions, UserAvatar } from '../../TaskTable/helpers/helper';
import { RowMenuDropdown } from '../../TaskTable/RowMenuDropdown';
import { BaseButton } from '../../UI/BaseButton';
import { CustomDatePicker } from '../../UI/CustomDatePicker/CustomDatePicker';
import { NoDragWrapper } from '../../UI/Dropdown';
import EpicDropdown from '../../UI/Dropdown/EpicDropdown';
import EstimateDropdown from '../../UI/Dropdown/EstimateDropdown';
import LabelDropdown from '../../UI/Dropdown/LabelDropdown';
import OwnerDropdown from '../../UI/Dropdown/OwnerDropdown';
import PriorityDropdown from '../../UI/Dropdown/PriorityDropdown';
import SprintStatusDropdown from '../../UI/Dropdown/SprintStatusDropdown';
import TaskStatusDropdown from '../../UI/Dropdown/TaskStatusDropdown';
import TaskTypeDropdown from '../../UI/Dropdown/TaskTypeDropdown';
import LabelChip, { LabelChipType } from '../../UI/LabelChip';
import LabelOverflowPill from '../../UI/LabelOverflowPill';
import ProgressLabel from '../../UI/ProgressLabel';
import { SprintStatusLabel } from '../../UI/SprintStatusLabel';
import StatusLabel, { StatusComponentType } from '../../UI/StatusLabel';
import { TaskEstimateComponentType, TaskEstimateLabel } from '../../UI/TaskEstimateLabel';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import TaskDependencyTooltip from '../../UI/Tooltip/TaskDependencyTooltip';
import { EpicLabelWithGoToIcon } from '../../UI/Dropdown/EpicDropdown';
import { PriorityLabel } from '../../UI/Dropdown/PriorityDropdown';
import { isEqual } from 'lodash';
import { getEffectiveSprintStatus, getPossibleSprintStatuses, sprintStatusToTaskUpdates } from '../../../utils/taskListUtils';
import Task from '../../../types/Task';
import { type RowWorkspaceData } from './types';
import { isColumnVisible, isExpandColumnVisible } from './columnVisibility';
import { queryClient } from '../../../queryClient';
import { queryKeys } from '../../../serverState/queryKeys';
import { useAddTaskDependency, useRemoveTaskDependency } from '../../../serverState/tasks';
import TaskDependencyDropdown from '../../UI/Dropdown/TaskDependencyDropdown';
import { TaskTypeLabel } from '../../UI/Dropdown/TaskTypeDropdown';
import { GoToIcon } from '../../../assets/CustomIcons/GoToIcon';
import { TaskDependencyDirection, TaskDependencyEdge } from '../../../types/TaskDependency';
import { SPACE_COLUMN_WIDTH } from '../../../utils/taskListUtils';

interface VirtualizedTaskRowProps {
  task: TaskTableDataType;
  isSubtask: boolean;
  hasSubtasks: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showMultiselect: boolean;
  isSelected: boolean;
  hasAnySelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onDeselect: (e: React.MouseEvent) => void;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  rowMenuProps: any;
  customRowSelection?: {
    selectedRowKeysObject: { [id: number]: boolean };
    addTaskToSelectedKeys: (task: Task) => void;
    removeTaskFromSelectedKeys: (task: Task) => void;
  };
  onTaskUpdate: (task: TaskTableDataType) => Promise<void>;
  onTaskOpen: (task: TaskTableDataType) => void;
  onSprintToPlanningConfirm?: (task: Task) => void;
  hiddenColumns?: Column[];
  columnsOrder?: Column[];
  showMessage: (message: string) => void;
  handleEditEstimationSettings?: () => void;
  titleWidth?: number;
  // Workspace data passed from parent to avoid per-row query subscriptions
  workspaceData: RowWorkspaceData;
  showSpaceColumn?: boolean;
  resolveSpaceName?: (workspaceId: number) => string;
  onEditingTitleChange?: (taskId: number, isEditing: boolean) => void;
}

/** Shared empty list so rows with no dependency cell open keep a stable `options` identity. */
const EMPTY_SPACE_TASKS: Task[] = [];

export const VirtualizedTaskRow: React.FC<VirtualizedTaskRowProps> = React.memo(({
  task,
  isSubtask,
  hasSubtasks,
  isExpanded,
  onToggleExpand,
  showMultiselect,
  isSelected,
  hasAnySelected,
  onSelect,
  onDeselect,
  usersWithAccessToSpace,
  rowMenuProps,
  customRowSelection,
  onTaskUpdate,
  onTaskOpen,
  onSprintToPlanningConfirm,
  hiddenColumns = [],
  columnsOrder = [],
  showMessage,
  handleEditEstimationSettings,
  titleWidth = 394,
  workspaceData,
  showSpaceColumn,
  resolveSpaceName,
  onEditingTitleChange,
}) => {
  const [form] = Form.useForm();
  // Borrowed/context rows (#2309) are shown in a group they don't belong to purely to
  // give a subitem its hierarchy. They stay undraggable — reordering is owned by the
  // home group — but they are selectable like any other row (#2596).
  const isGroupContext = !!task.isGroupContext;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: isGroupContext,
    data: {
      type: 'Task',
      task,
    },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    cursor: isGroupContext ? 'default' : isDragging ? 'grabbing' : 'grab',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  const { openModal } = useContext(ModalContext);
  const viewContext = useViewContext();
  const navigateToTask = useNavigateToTask();

  const epics = workspaceData.epics;
  const framework = workspaceData.framework;
  const estimationType = workspaceData.estimationType;
  const taskStatuses = workspaceData.taskStatuses;
  const activeSprintId = workspaceData.activeSprintId;
  const planningSessionId = workspaceData.planningSessionId;

  // Dependency mutations + the space task list (resolved from cache — RowWorkspaceData
  // carries no task list) so the Blocking / Blocked By cells can pick & display.
  const addDependency = useAddTaskDependency(task.id, task.workspaceId);
  const removeDependency = useRemoveTaskDependency(task.id, task.workspaceId);

  // Editing state for different cells
  const [editingCell, setEditingCell] = useState<string | null>(null);
  // Which empty dependency cell (Blocking or Blocked By) is hovered — swaps its static
  // "None" text to the "Choose Blockers" CTA copy on hover (no tooltip). (#2614)
  const [hoveredEmptyDependencyCell, setHoveredEmptyDependencyCell] = useState<TaskDependencyDirection | null>(null);

  // Only the row whose dependency cell is being edited needs the space's task list, and it
  // needs the same array identity for as long as the dropdown stays open — otherwise the
  // dropdown's frozen ordering recomputes on every render (#2601) and every row in the
  // table rebuilds a multi-thousand-element array on every render (#2625).
  const isEditingDependencyCell = editingCell === 'blocking' || editingCell === 'blockedBy';
  const spaceTasks = useMemo(
    () =>
      isEditingDependencyCell
        ? (queryClient.getQueryData<Task[]>(queryKeys.tasks(task.workspaceId)) ?? []).filter(
            (t) => t.type !== TaskType.epic
          )
        : EMPTY_SPACE_TASKS,
    [isEditingDependencyCell, task.workspaceId]
  );
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const titleTextRef = useRef<HTMLDivElement>(null);
  const [isSpaceTruncated, setIsSpaceTruncated] = useState(false);
  const spaceTextRef = useRef<HTMLSpanElement>(null);
  const savingRef = useRef(false);
  const handleBlur = useCallback(() => {
    if (!savingRef.current) {
      setEditingCell(null);
    }
  }, []);
  const inputRef = useRef<TextAreaRef>(null);

  useEffect(() => {
    if (editingCell && inputRef.current) {
      // Place the caret at the end of the title instead of the start. DEV-2210
      inputRef.current.focus({ cursor: 'end' });
    }
  }, [editingCell]);

  // Let the list lock scrolling while the title is being renamed, so scrolling
  // the row out of the viewport can't silently discard the in-progress edit (#2210).
  useEffect(() => {
    onEditingTitleChange?.(task.id, editingCell === 'title');
    return () => {
      onEditingTitleChange?.(task.id, false);
    };
  }, [editingCell, onEditingTitleChange, task.id]);

  useEffect(() => {
    if (editingCell === 'title') {
      const handleMouseDown = (e: MouseEvent) => {
        const inputEl = inputRef.current?.resizableTextArea?.textArea;
        if (inputEl && !inputEl.contains(e.target as Node)) {
          e.preventDefault();
          e.stopPropagation();
          document.addEventListener('click', (ce) => ce.stopPropagation(), { once: true, capture: true });
          inputRef.current?.blur();
        }
      };
      document.addEventListener('mousedown', handleMouseDown, true);
      return () => document.removeEventListener('mousedown', handleMouseDown, true);
    }
  }, [editingCell]);

  const handleSave = useCallback(
    async (updates: Partial<TaskTableDataType>) => {
      savingRef.current = true;
      // Exit edit mode immediately so the green active border around the property
      // cell clears instantly on select instead of lingering for the duration of
      // the (optimistic) save round-trip. #2244
      setEditingCell(null);
      try {
        if (!isEqual({ ...task, ...updates }, task)) {
          await onTaskUpdate({ ...task, ...updates });
        }
      } catch (error) {
        console.error('Save failed:', error);
      }
      savingRef.current = false;
    },
    [task, onTaskUpdate]
  );

  // Label cell persists changes WITHOUT exiting edit mode, so a checkbox toggle
  // keeps the dropdown open (DEV-2606). Closing is driven by the dropdown itself
  // (label-text click / outside click) through handleBlur — matching task view.
  const handleLabelSave = useCallback(
    async (updates: Partial<TaskTableDataType>) => {
      try {
        if (!isEqual({ ...task, ...updates }, task)) {
          await onTaskUpdate({ ...task, ...updates });
        }
      } catch (error) {
        console.error('Save failed:', error);
      }
    },
    [task, onTaskUpdate]
  );

  const isInSprint = task.sprintId && task.sprintId === activeSprintId;

  // Handle mouse down for drag - ignore certain elements
  const handleMouseDown = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    let element = target;

    while (element && element !== event.currentTarget) {
      const className = element.className || '';
      const classString = typeof className === 'string' ? className : String(className);

      if (
        classString.includes('ant-dropdown') ||
        classString.includes('ant-select') ||
        classString.includes('ant-input') ||
        editingCell !== null
      ) {
        return;
      }

      element = element.parentElement as HTMLElement;
    }

    if (listeners?.onMouseDown) {
      listeners.onMouseDown(event);
    }
  };

  const dragHandleListeners = {
    ...listeners,
    onMouseDown: handleMouseDown,
  };

  const operationWidth = customRowSelection ? 86 : 68;
  // Selection is keyed by task id, so a borrowed row and its home copy check together (#2596).
  const isRowSelected = isSelected || !!customRowSelection?.selectedRowKeysObject[task.id];
  const stickyBg = isRowSelected ? 'bg-blue-100 group-hover:bg-blue-100' : 'bg-white group-hover:bg-neutral-50';
  // Shared with StickyColumnHeader so the header and this row never disagree. (#2544)
  const visibilityContext = {
    hiddenColumns,
    viewEntityType: viewContext?.view?.entityType,
    viewGroup: viewContext?.view?.group,
    framework,
  };
  const showColumn = (column: Column) => isColumnVisible(column, visibilityContext);
  const showExpandColumn = isExpandColumnVisible(visibilityContext);
  const expandWidth = showExpandColumn ? 20 : 0;

  const renderCell = (column: string) => {
    const borderLeft = 'border-0 border-l border-solid border-neutral-200';

    // Shared renderer for the Blocking / Blocked By cells. Edges come straight off
    // the self-describing list payload (task.BlockingEdges / task.BlockedByEdges).
    const renderDependencyCell = (
      edges: TaskDependencyEdge[],
      direction: TaskDependencyDirection,
      widthKey: 'blocking' | 'blockedBy'
    ) => {
      // Hide self + tasks linked in the OPPOSITE direction (can't both block and be
      // blocked by the same task); same-direction links stay visible, rendered checked.
      const oppositeIds =
        direction === 'blocking'
          ? (task.BlockedByEdges ?? []).map((e) => e.Blocking.id)
          : (task.BlockingEdges ?? []).map((e) => e.Blocked.id);
      const excludeTaskIds = [task.id, ...oppositeIds];
      // The cell's display is identical whether or not the dropdown is open — clicking
      // must NOT change the text (matches the Epic cell, which keeps "No epic" while editing).
      const display =
        edges.length === 0 ? (
          <div
            className="text-neutral-700 p-1"
            onMouseEnter={() => setHoveredEmptyDependencyCell(direction)}
            onMouseLeave={() => setHoveredEmptyDependencyCell(null)}
          >
            {hoveredEmptyDependencyCell === direction ? 'Choose Blockers' : 'None'}
          </div>
        ) : edges.length === 1 ? (
          // Single link: the value keeps its in-cell "go to" arrow, and hovering reveals
          // the untruncated title. (#2599)
          <CustomTooltip title={edges[0].task.title}>
            <div className="relative parent-task flex items-center gap-1 min-w-0 max-w-full px-1">
              <TaskTypeLabel type={edges[0].task.type as TaskType} size="small" />
              <span className="truncate min-w-0 text-neutral-900 text-sm">{edges[0].task.title}</span>
              <GoToIcon
                className="absolute right-0 top-1/2 -translate-y-1/2 text-neutral-900 bg-neutral-300 rounded p-1 cursor-pointer parent-task__go-to-icon"
                onClick={(e: React.MouseEvent<SVGSVGElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigateToTask({ workspaceId: task.workspaceId, taskId: edges[0].task.taskId });
                }}
              />
            </div>
          </CustomTooltip>
        ) : (
          // Several links: the cell never grows past "first + N" — hovering anywhere in it
          // lists them all, each row offering "go to" and "remove". (#2599)
          <TaskDependencyTooltip
            edges={edges}
            onOpenTask={(related) => navigateToTask({ workspaceId: task.workspaceId, taskId: related.taskId })}
            onRemove={(dependencyId, relatedTaskId) => removeDependency.mutate({ dependencyId, relatedTaskId })}
          >
            <div className="flex items-center gap-1 min-w-0 max-w-full px-1">
              <TaskTypeLabel type={edges[0].task.type as TaskType} size="small" />
              <span className="truncate min-w-0 text-neutral-900 text-sm">{edges[0].task.title}</span>
              <span className="text-neutral-700 text-xs shrink-0">+{edges.length - 1}</span>
            </div>
          </TaskDependencyTooltip>
        );
      return (
        <div
          style={{
            width: `${COLUMN_WIDTHS[widthKey]}px`,
            minWidth: `${COLUMN_WIDTHS[widthKey]}px`,
            boxShadow: editingCell === widthKey ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
          }}
          className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
        >
          {editingCell === widthKey ? (
            <TaskDependencyDropdown
              options={spaceTasks}
              epics={epics}
              excludeTaskIds={excludeTaskIds}
              selectedEdges={edges}
              defaultOpen
              onClose={() => setEditingCell(null)}
              onAdd={(selected) => addDependency.mutate({ relatedTaskId: selected.id, type: direction })}
              onRemove={(dependencyId, relatedTaskId) => removeDependency.mutate({ dependencyId, relatedTaskId })}
              onRemoveAll={() =>
                edges.forEach((e) => removeDependency.mutate({ dependencyId: e.id, relatedTaskId: e.task.id }))
              }
              removeAllLabel={direction === 'blockedBy' ? 'Remove All Blockers' : 'Remove All Blocking'}
              trigger={<div className="w-full h-full min-w-0 flex items-center justify-center">{display}</div>}
            />
          ) : (
            <div
              className="w-full h-full min-w-0 flex justify-center items-center cursor-pointer"
              onClick={() => setEditingCell(widthKey)}
            >
              {display}
            </div>
          )}
        </div>
      );
    };

    switch (column) {
      case 'operation':
        return (
          <div
            style={{ width: `${operationWidth}px`, minWidth: `${operationWidth}px` }}
            className={`flex items-center flex-shrink-0 sticky px-2 left-0 z-10 gap-2 h-full ${stickyBg}`}
          >
            <RowMenuDropdown menuProps={rowMenuProps} className="operation_items ml-2" />
            {showMultiselect && isSelected && (
              <CheckSquareFilled
                style={{ fontSize: '16px' }}
                className="text-blue-500 cursor-pointer"
                onClick={onDeselect}
              />
            )}
            {showMultiselect && !isSelected && (
              <div
                className={`${hasAnySelected ? '' : 'operation_items'} h-4 w-4 rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500 cursor-pointer`}
                onClick={onSelect}
              />
            )}

            {customRowSelection && customRowSelection.selectedRowKeysObject[task.id] && (
              <CustomTooltip title={'Click to remove from Planning'}>
                <div
                  className="h-4 w-4 flex items-center justify-center rounded-sm bg-blue-500 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    customRowSelection.removeTaskFromSelectedKeys(task);
                  }}
                >
                  <ClockCircleOutlined className="w-3 h-3 text-white" />
                </div>
              </CustomTooltip>
            )}

            {customRowSelection && !customRowSelection.selectedRowKeysObject[task.id] && (
              <CustomTooltip title={isInSprint ? 'Click to move to Planning' : 'Click to add to Planning'}>
                <div
                  className={`h-4 w-4 flex items-center justify-center rounded-sm border-box border border-solid border-neutral-600 hover:border-blue-500 cursor-pointer ${isInSprint ? 'bg-yellow-200 border-yellow-200 hover:border-yellow-200' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    customRowSelection.addTaskToSelectedKeys(task);
                  }}
                >
                  {isInSprint && <SprintIcon className="w-3 h-3 text-yellow-600" />}
                </div>
              </CustomTooltip>
            )}
          </div>
        );

      case 'taskId':
        return (
          <div
            style={{ width: `${COLUMN_WIDTHS.taskId}px`, minWidth: `${COLUMN_WIDTHS.taskId}px`, left: operationWidth }}
            className={`flex items-center justify-between flex-shrink-0 sticky z-10 pl-2 h-full ${stickyBg} taskId_container`}
          >
            <div className="text-neutral-900 cursor-default">{`DEV-${task.taskId}`}</div>
            <div
              className="w-5 h-5 flex items-center justify-center rounded taskId_link cursor-pointer"
              onClick={async () => {
                const pathname = location.pathname.split('/');
                pathname.pop();
                await navigator.clipboard.writeText(`${getWebsiteDomain()}${pathname.join('/')}/task/${task.taskId}`);
                showMessage('Task link copied!');
              }}
            >
              <LinkOutlined className="text-neutral-900" />
            </div>
          </div>
        );

      case 'expand':
        if (showExpandColumn) {
          return (
            <div
              style={{ width: '20px', minWidth: '20px', left: operationWidth + COLUMN_WIDTHS.taskId }}
              className={`flex items-center justify-center flex-shrink-0 sticky z-10 ${stickyBg} h-10`}
            >
              {hasSubtasks && (
                <div className="flex items-center justify-center cursor-pointer" onClick={onToggleExpand}>
                  <Icon component={ChevronLeftSvg} rotate={isExpanded ? 270 : 180} />
                </div>
              )}
            </div>
          );
        }
        return null;

      case 'title':
        return (
          <div
            style={{
              width: `${titleWidth}px`,
              minWidth: '394px',
              left: operationWidth + COLUMN_WIDTHS.taskId + expandWidth,
            }}
            className={`sticky-title-cell ${editingCell === 'title' ? 'editing-title' : 'overflow-hidden'} flex items-center flex-shrink-0 sticky z-10 ${stickyBg} h-10`}
            onClick={() => {
              if (editingCell !== 'title') {
                onTaskOpen(task);
              }
            }}
          >
            {isSubtask && <SubitemIcon className="text-neutral-600 mr-2 ml-1 shrink-0" />}
            {viewContext?.view?.entityType === WorkspaceViewEntityType.epic ? (
              // In the Epics view the type can't be changed, so there is no dropdown to
              // protect from the row's drag. TaskTypeDropdown's NoDragWrapper stops
              // pointerdown, which is dnd-kit's drag activator — that made the epic icon
              // the one spot on the row you couldn't grab to reorder (#2398). Render the
              // inert label instead; clicks stay swallowed, as they were.
              <div className="h-full flex items-center" onClick={(e) => e.stopPropagation()}>
                <TaskTypeLabel type={task.type as TaskType} size="small" />
              </div>
            ) : (
              <TaskTypeDropdown
                type={task.type as TaskType}
                size="small"
                onBlur={() => {
                  setEditingCell(null);
                }}
                onSelect={(type) => {
                  void handleSave({ type });
                }}
                position="bottom"
              />
            )}
            {editingCell === 'title' ? (
              <div className="relative flex-1 min-w-0 h-full">
                <Input.TextArea
                  size={'small'}
                  ref={inputRef}
                  defaultValue={task.title}
                  // Floating overlay (DEV-2210): grows downward only as an absolutely-
                  // positioned overlay so it never shifts the row or any other column/
                  // cell. Left+right anchoring makes its width follow the title cell
                  // instead of a fixed size that overflowed the cell.
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  // left-0 aligns the input's text with the static title text (the
                  // input's border+padding matches the display text's px-2, so there's
                  // no horizontal jump on edit); top-2 vertically centers it in the 40px
                  // row; 10px right gap. Multi-line grows downward from here. DEV-2210
                  // antd's Input.TextArea sets `width: 100%` in its own CSS, which beats
                  // the `right-2.5` offset (over-constrained box, `right` gets dropped) —
                  // an explicit width is needed for the 10px gap to actually apply.
                  className="!absolute !left-0 !top-2 !z-20 shadow-md"
                  style={{ minHeight: '22px', width: 'calc(100% - 10px)' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setEditingCell(null);
                      return;
                    }
                    // Enter submits; Shift+Enter inserts a newline.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      const newValue = (e.target as HTMLTextAreaElement).value.trim();
                      if (newValue) {
                        void handleSave({ title: newValue });
                      } else {
                        setEditingCell(null);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const newValue = (e.target as HTMLTextAreaElement).value.trim();
                    if (newValue) {
                      void handleSave({ title: newValue });
                    } else {
                      setEditingCell(null);
                    }
                  }}
                  data-no-dnd="true"
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <div className="relative flex items-center min-w-0 w-full group">
                <CustomTooltip title={isTitleTruncated ? task.title : ''}>
                  <div
                    ref={titleTextRef}
                    className="truncate overflow-hidden whitespace-nowrap py-[4px] px-[8px] rounded"
                    onMouseEnter={() => {
                      if (titleTextRef.current) {
                        setIsTitleTruncated(titleTextRef.current.scrollWidth > titleTextRef.current.clientWidth);
                      }
                    }}
                  >
                    {task.title}
                  </div>
                </CustomTooltip>
                <div
                  task-edit-id={task.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCell('title');
                  }}
                />
                <BaseButton
                  type="secondary"
                  size="small"
                  className="open_task_details_button ml-2 text-neutral-900 absolute top-1/2 -translate-y-1/2 right-2 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCell('title');
                  }}
                >
                  <EditOutlined />
                </BaseButton>
              </div>
            )}
          </div>
        );

      case 'subtasks':
        if (!showColumn(Column.subitems)) return null;
        const subtasks = task.subtasks || [];
        const completedSubtasks = subtasks.filter(
          (subtask) => taskStatuses[subtask.statusId]?.group === TaskStatusGroup.completed
        );
        return (
          <div
            style={{ width: `${COLUMN_WIDTHS.subitems}px`, minWidth: `${COLUMN_WIDTHS.subitems}px` }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft} ${!isSubtask ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={(e) => {
              if (!isSubtask) {
                e.stopPropagation();
                openModal(ModalType.CreateItem, buildAddSubitemModalProps(task));
              }
            }}
          >
            {!isSubtask && (
              <ProgressLabel task={task} completedSubtasks={completedSubtasks.length} subtasks={subtasks.length} />
            )}
          </div>
        );

      case 'epicId':
        if (!showColumn(Column.epic)) return null;
        const epic = epics.find((p) => p.id === Number(task.epicId));
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.epicId}px`,
              minWidth: `${COLUMN_WIDTHS.epicId}px`,
              boxShadow: editingCell === 'epicId' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'epicId' ? (
              <EpicDropdown
                epic={epic || null}
                epics={epics}
                onBlur={handleBlur}
                onSelect={async (epic) => {
                  await handleSave({ epicId: epic?.id || null });
                }}
                onOpenEpic={(epic) => navigateToTask(epic)}
                size="medium"
                defaultOpen={true}
                emptyElement={<div className="text-neutral-700 px-1 py-[11px]">No epic</div>}
                showGoToIcon
                className="h-full w-full"
                hideOutline
                position="bottom"
              />
            ) : (
              <div
                className="w-full h-full flex justify-center items-center cursor-pointer"
                onClick={() => {
                  if (!task.parentTaskId) {
                    setEditingCell('epicId');
                  }
                }}
              >
                {!epic && <div className="text-neutral-700 p-1">No epic</div>}
                {epic && (
                  <EpicLabelWithGoToIcon
                    epic={epic}
                    size="medium"
                    disabled={!!task.parentTaskId}
                    onOpenEpic={() => navigateToTask(epic)}
                    className="h-full"
                  />
                )}
              </div>
            )}
          </div>
        );

      case 'blocking':
        if (!showColumn(Column.blocking)) return null;
        return renderDependencyCell(
          (task.BlockingEdges ?? []).map((e) => ({ id: e.id, task: e.Blocked })),
          'blocking',
          'blocking'
        );

      case 'blockedBy':
        if (!showColumn(Column.blockedBy)) return null;
        return renderDependencyCell(
          (task.BlockedByEdges ?? []).map((e) => ({ id: e.id, task: e.Blocking })),
          'blockedBy',
          'blockedBy'
        );

      case 'sprintId': {
        if (!showColumn(Column.sprintStatus)) return null;
        const isScrum = framework === WorkspaceFramework.scrum;
        const possibleSprintStatuses = getPossibleSprintStatuses(isScrum, activeSprintId);

        const currentSprintStatus = getEffectiveSprintStatus({ task, isScrum, activeSprintId });

        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.sprintId}px`,
              minWidth: `${COLUMN_WIDTHS.sprintId}px`,
              boxShadow: editingCell === 'sprintId' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-start flex-shrink-0 pl-2 h-10 ${borderLeft}`}
          >
            {editingCell === 'sprintId' ? (
              <SprintStatusDropdown
                defaultOpen={true}
                sprintStatus={currentSprintStatus}
                sprintStatuses={possibleSprintStatuses.map((p) => p.sprintStatus)}
                onSelect={(sprintStatus) => {
                  if (!sprintStatus) return;
                  if (
                    sprintStatus === 'planning' &&
                    task.stage === 'sprint' &&
                    task.sprintId === activeSprintId &&
                    onSprintToPlanningConfirm
                  ) {
                    setEditingCell(null);
                    onSprintToPlanningConfirm(task);
                    return;
                  }
                  void handleSave(sprintStatusToTaskUpdates(sprintStatus, activeSprintId));
                }}
                onBlur={handleBlur}
                size="medium"
                className="h-full w-full"
                hideOutline
                position="bottom"
              />
            ) : (
              <div
                className="w-full h-full flex items-center cursor-pointer"
                onClick={() => setEditingCell('sprintId')}
              >
                <SprintStatusLabel
                  sprintStatus={currentSprintStatus as any}
                  component="table"
                />
              </div>
            )}
          </div>
        );
      }

      case 'TaskLabels':
        if (!showColumn(Column.label)) return null;
        return (
          <MemoizedLabelCell
            task={task}
            workspaceLabels={workspaceData.labels}
            editingCell={editingCell}
            setEditingCell={setEditingCell}
            handleSave={handleLabelSave}
            handleBlur={handleBlur}
            borderLeft={borderLeft}
          />
        );

      case 'assignee':
        if (!showColumn(Column.assignee)) return null;
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.assignee}px`,
              minWidth: `${COLUMN_WIDTHS.assignee}px`,
              boxShadow: editingCell === 'assignee' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'assignee' ? (
              <OwnerDropdown
                owner={
                  usersWithAccessToSpace.find((userAccess) => userAccess.userId === Number(task.assignee))?.User || null
                }
                owners={usersWithAccessToSpace.map((userAccess) => userAccess.User)}
                onBlur={handleBlur}
                onSelect={(user) => {
                  void handleSave({ assignee: user?.id || null });
                }}
                size="small"
                defaultOpen={true}
                selectedElement={<UserAvatar assignee={task.assignee ?? undefined} usersWithAccessToSpace={usersWithAccessToSpace} />}
                className="h-full w-full"
                hideOutline
                position="bottom"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={() => setEditingCell('assignee')}
              >
                <UserAvatar assignee={task.assignee ?? undefined} usersWithAccessToSpace={usersWithAccessToSpace} />
              </div>
            )}
          </div>
        );

      case 'statusId':
        if (!showColumn(Column.status)) return null;
        const status = taskStatuses[task.statusId] || defaultTaskStatuses[0];
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.statusId}px`,
              minWidth: `${COLUMN_WIDTHS.statusId}px`,
            }}
            className={`relative flex items-center justify-start flex-shrink-0 pl-2 h-10 overflow-hidden ${borderLeft}`}
          >
            {/* Active ring as an overlay so it always paints on top of the status pill's background (the pill's
                bg otherwise covers the right edge of an inset outline because overflow-hidden clips at the border box). */}
            {editingCell === 'statusId' && (
              <div
                className="pointer-events-none absolute inset-0 z-10"
                style={{ border: `2px solid ${colorTokens.green[300]}` }}
              />
            )}
            {editingCell === 'statusId' ? (
              <TaskStatusDropdown
                taskStatusId={task.statusId}
                workspaceId={task.workspaceId}
                onSelect={(status) => {
                  void handleSave({ statusId: status.id });
                }}
                onBlur={handleBlur}
                size="medium"
                defaultOpen={true}
                className="h-full w-full"
                hideOutline
                position="bottom"
                triggerComponentType={StatusComponentType.Table}
              />
            ) : (
              <div
                className="w-full h-full flex justify-start items-center cursor-pointer"
                onClick={() => setEditingCell('statusId')}
              >
                <StatusLabel
                  color={status.color}
                  title={status.title}
                  group={status.group}
                  component={StatusComponentType.Table}
                />
              </div>
            )}
          </div>
        );

      case 'startDate':
        if (!showColumn(Column.startDate)) return null;
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.startDate}px`,
              minWidth: `${COLUMN_WIDTHS.startDate}px`,
              boxShadow: editingCell === 'startDate' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'startDate' ? (
              <NoDragWrapper>
                <CustomDatePicker
                  onChange={(date) => {
                    void handleSave({
                      startDate: date !== null ? (date.toDate().toISOString() as any) : null,
                    });
                  }}
                  onClose={handleBlur}
                  startDate={task.startDate ? (typeof task.startDate === 'string' ? new Date(task.startDate) : task.startDate) : null}
                  targetDate={task.deadline ? (typeof task.deadline === 'string' ? new Date(task.deadline) : task.deadline) : null}
                  mode="startDate"
                  placeholder="Set date"
                  className="h-full w-full"
                  hideOutline
                />
              </NoDragWrapper>
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={() => setEditingCell('startDate')}
              >
                {task.startDate ? (
                  <div className="text-neutral-900">{dayjs(task.startDate).format('MMM DD, YYYY')}</div>
                ) : (
                  <div className="text-neutral-700">No date</div>
                )}
              </div>
            )}
          </div>
        );

      case 'deadline':
        if (!showColumn(Column.deadline)) return null;
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.deadline}px`,
              minWidth: `${COLUMN_WIDTHS.deadline}px`,
              boxShadow: editingCell === 'deadline' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'deadline' ? (
              <NoDragWrapper>
                <CustomDatePicker
                  onChange={(date) => {
                    void handleSave({
                      deadline: date !== null ? (date.toDate().toISOString() as any) : null,
                    });
                  }}
                  onClose={handleBlur}
                  startDate={task.startDate ? (typeof task.startDate === 'string' ? new Date(task.startDate) : task.startDate) : null}
                  targetDate={task.deadline ? (typeof task.deadline === 'string' ? new Date(task.deadline) : task.deadline) : null}
                  mode="targetDate"
                  placeholder="Set date"
                  className="h-full w-full"
                  hideOutline
                />
              </NoDragWrapper>
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={() => setEditingCell('deadline')}
              >
                {task.deadline ? (
                  <div className="text-neutral-900">{dayjs(task.deadline).format('MMM DD, YYYY')}</div>
                ) : (
                  <div className="text-neutral-700">No date</div>
                )}
              </div>
            )}
          </div>
        );

      case 'priority':
        if (!showColumn(Column.priority)) return null;
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.priority}px`,
              minWidth: `${COLUMN_WIDTHS.priority}px`,
              boxShadow: editingCell === 'priority' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'priority' ? (
              <PriorityDropdown
                priority={task.priority as PriorityOptions}
                size="medium"
                onSelect={(priority) => {
                  void handleSave({ priority });
                }}
                onBlur={handleBlur}
                defaultOpen={true}
                className="h-full w-full"
                hideOutline
                position="bottom"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={() => setEditingCell('priority')}
              >
                <PriorityLabel priority={task.priority as PriorityOptions} size="medium" />
              </div>
            )}
          </div>
        );

      case 'estimate':
      case 'storyPoints':
        if (!showColumn(Column.estimate)) return null;
        const estimateValue = estimationType === WorkspaceEstimationType.storyPoints ? task.storyPoints : task.estimate;
        return (
          <div
            style={{
              width: `${COLUMN_WIDTHS.estimate}px`,
              minWidth: `${COLUMN_WIDTHS.estimate}px`,
              boxShadow: editingCell === 'estimate' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
            }}
            className={`flex items-center justify-center flex-shrink-0 h-10 ${borderLeft}`}
          >
            {editingCell === 'estimate' ? (
              <EstimateDropdown
                estimate={estimateValue ?? null}
                size="small"
                onSelect={(estimate) => {
                  const field = estimationType === WorkspaceEstimationType.storyPoints ? 'storyPoints' : 'estimate';
                  void handleSave({ [field]: estimate });
                }}
                onBlur={handleBlur}
                defaultOpen={true}
                onEditEstimationSettings={handleEditEstimationSettings}
                selectedElement={
                  <TaskEstimateLabel
                    estimate={estimateValue ?? undefined}
                    component={TaskEstimateComponentType.table}
                    estimationType={estimationType}
                  />
                }
                className="h-full w-full"
                hideOutline
                position="bottom"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center cursor-pointer"
                onClick={() => setEditingCell('estimate')}
              >
                <TaskEstimateLabel
                  estimate={estimateValue ?? undefined}
                  component={TaskEstimateComponentType.table}
                  estimationType={estimationType}
                />
              </div>
            )}
          </div>
        );

      case 'space-label':
        if (!showColumn(Column.space)) return null;
        return (
          <div
            style={{ width: `${SPACE_COLUMN_WIDTH}px`, minWidth: `${SPACE_COLUMN_WIDTH}px` }}
            className="flex items-center flex-shrink-0 h-10 pl-2 border-0 border-l border-solid border-neutral-200 text-brand-grey-dark text-sm truncate overflow-hidden"
          >
            <CustomTooltip title={isSpaceTruncated ? resolveSpaceName?.(task.workspaceId) ?? '' : ''}>
              <span
                ref={spaceTextRef}
                className="truncate overflow-hidden whitespace-nowrap"
                onMouseEnter={() => {
                  if (spaceTextRef.current) {
                    setIsSpaceTruncated(spaceTextRef.current.scrollWidth > spaceTextRef.current.clientWidth);
                  }
                }}
              >
                {resolveSpaceName?.(task.workspaceId) ?? ''}
              </span>
            </CustomTooltip>
          </div>
        );

      case 'space':
        return (
          <div
            style={{ width: `${COLUMN_WIDTHS.space}px`, minWidth: `${COLUMN_WIDTHS.space}px` }}
            className="flex-shrink-0 h-10"
          />
        );

      default:
        return null;
    }
  };

  // Map from render key to Column enum for ordering
  const columnKeyMap: Record<string, Column> = {
    'space-label': Column.space,
    subtasks: Column.subitems,
    epicId: Column.epic,
    sprintId: Column.sprintStatus,
    TaskLabels: Column.label,
    assignee: Column.assignee,
    statusId: Column.status,
    startDate: Column.startDate,
    deadline: Column.deadline,
    priority: Column.priority,
    estimate: Column.estimate,
    storyPoints: Column.estimate,
    blocking: Column.blocking,
    blockedBy: Column.blockedBy,
  };

  const frozenColumns = [
    'operation',
    'taskId',
    ...(showExpandColumn ? ['expand'] : []),
    'title',
  ];

  const reorderableColumns = [
    ...(showSpaceColumn ? ['space-label'] : []),
    'subtasks',
    'epicId',
    'sprintId',
    'TaskLabels',
    'assignee',
    'statusId',
    'priority',
    estimationType === WorkspaceEstimationType.tshirtSizes ? 'estimate' : 'storyPoints',
    'startDate',
    'deadline',
    'blocking',
    'blockedBy',
    'space',
  ];

  // Sort reorderable columns by columnsOrder
  const sortedReorderableColumns = columnsOrder.length > 0
    ? [...reorderableColumns].sort((a, b) => {
        const colA = columnKeyMap[a];
        const colB = columnKeyMap[b];
        const indexA = colA ? columnsOrder.indexOf(colA) : -1;
        const indexB = colB ? columnsOrder.indexOf(colB) : -1;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      })
    : reorderableColumns;

  const visibleColumns = [...frozenColumns, ...sortedReorderableColumns];

  // While renaming, let the row grow (min-h-10) so the title textarea can expand
  // into further rows; otherwise keep the fixed 40px row height. (DEV-2210)
  const isEditingTitle = editingCell === 'title';
  const rowClassName = `editable-row ${isEditingTitle ? 'min-h-10' : 'h-10'} ${isRowSelected ? 'selected-row' : ''} ${task.isDisabledInFilter ? 'opacity-50' : ''}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...dragHandleListeners}
      onDragStart={(e) => e.preventDefault()}
      className={`${rowClassName} flex items-center ${isRowSelected ? 'bg-blue-100 hover:bg-blue-100' : 'bg-white hover:bg-neutral-50'} group`}
    >
      <Form form={form} component={false}>
        {visibleColumns.map((col) => (
          <React.Fragment key={col}>{renderCell(col)}</React.Fragment>
        ))}
      </Form>
    </div>
  );
}, (prevProps, nextProps) => {
  if (prevProps.task !== nextProps.task) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.isExpanded !== nextProps.isExpanded) return false;
  if (prevProps.showMultiselect !== nextProps.showMultiselect) return false;
  if (prevProps.hasAnySelected !== nextProps.hasAnySelected) return false;
  if (prevProps.isSubtask !== nextProps.isSubtask) return false;
  if (prevProps.hasSubtasks !== nextProps.hasSubtasks) return false;
  if (prevProps.titleWidth !== nextProps.titleWidth) return false;
  if (prevProps.showSpaceColumn !== nextProps.showSpaceColumn) return false;
  if (prevProps.workspaceData !== nextProps.workspaceData) return false;
  // Callback props (onToggleExpand, onSelect, onDeselect, rowMenuProps,
  // onTaskOpen, showMessage, etc.) get new references on every parent render
  // but don't affect visual output — skip comparing them.
  return true;
});

/** Memoized label cell — prevents re-render when unrelated workspace data changes */
const MemoizedLabelCell = React.memo(
  ({
    task,
    workspaceLabels,
    editingCell,
    setEditingCell,
    handleSave,
    handleBlur,
    borderLeft,
  }: {
    task: TaskTableDataType;
    workspaceLabels: SpaceLabel[];
    editingCell: string | null;
    setEditingCell: (cell: string | null) => void;
    handleSave: (updates: any) => Promise<void>;
    handleBlur: () => void;
    borderLeft: string;
  }) => {
    const taskLabels =
      workspaceLabels
        .filter((label) => task.TaskLabels?.some((tl: any) => tl.labelId === label.id))
        .sort((a, b) => (a?.orderIndex ?? a?.id ?? 0) - (b?.orderIndex ?? b?.id ?? 0)) || [];

    return (
      <div
        style={{
          width: `${COLUMN_WIDTHS.TaskLabels}px`,
          minWidth: `${COLUMN_WIDTHS.TaskLabels}px`,
          boxShadow: editingCell === 'TaskLabels' ? `inset 0 0 0 2px ${colorTokens.green[300]}` : 'none',
        }}
        className={`flex items-center flex-shrink-0 h-10 pl-2 ${borderLeft}`}
      >
        {editingCell === 'TaskLabels' ? (
          <LabelDropdown
            selectedLabels={taskLabels}
            availableLabels={workspaceLabels}
            onSelectionChange={async (labels) => {
              await handleSave({
                TaskLabels: labels.map((label) => ({ id: 0, labelId: label.id, taskId: task.id })),
              });
            }}
            onBlur={handleBlur}
            size="small"
            defaultOpen={true}
            placeholder="No label"
            className="h-full w-full"
            hideOutline
            customAlign
            shiftHorizontally
          />
        ) : (
          <div
            className="w-full h-full flex items-center gap-1 cursor-pointer"
            onClick={() => setEditingCell('TaskLabels')}
          >
            {taskLabels.length > 0 ? (
              <>
                {taskLabels[0] && <LabelChip key={taskLabels[0].id} label={taskLabels[0]} type={LabelChipType.Table} />}
                <LabelOverflowPill hiddenLabels={taskLabels.slice(1)} />
              </>
            ) : (
              <div className="text-neutral-700 px-2">No label</div>
            )}
          </div>
        )}
      </div>
    );
  },
  (prev, next) => {
    if (prev.editingCell !== next.editingCell) return false;
    if (prev.borderLeft !== next.borderLeft) return false;
    // Skip re-render if TaskLabels was temporarily lost during a transient update
    if (!next.task.TaskLabels && prev.task.TaskLabels) return true;
    const prevIds = prev.task.TaskLabels?.map((tl: any) => tl.labelId).join(',') ?? '';
    const nextIds = next.task.TaskLabels?.map((tl: any) => tl.labelId).join(',') ?? '';
    if (prevIds !== nextIds) return false;
    return true;
  }
);
